import { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { Check, Info } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { getAccount, type AccountDetail } from "@/lib/brokerAccounts";
import {
  ADVANCE_LINK_COPY,
  ADVANCE_LINK_KINDS,
  advanceApplication,
  advanceLinkRecipients,
  advanceLinkRow,
  connectionsByKind,
  listAccountConnections,
  requestAccountConnection,
  sendAdvanceLink,
  type AccountConnection,
  type AdvanceApplicationView,
  type AdvanceLinkRecipient,
  type AdvanceStep,
} from "@/lib/advanceApplication";
import {
  ADVANCE_WITH_UNDERWRITER,
  readAdvanceRequirements,
  type AdvanceEvidenceRequirement,
  type ReadAdvanceRequirements,
} from "@/lib/signalAdvanceEvidence";
import { readPreapprovalApplication } from "@/lib/preapprovalApplication";
import { messageFor } from "@/lib/errors";
import { formatShortDate, statusBadgeClass } from "@/lib/listFormat";
import { buttonClass, TOUCH_SQUARE } from "@/components/buttonStyles";
import { SelectField } from "@/components/SelectField";
import { SignalAdvanceEvidence } from "@/components/SignalAdvanceEvidence";
import { WELL } from "@/base";

// BRK-S407 — the Surety program tab's SIGNAL ADVANCE APPLICATION section,
// `#advance-app`: the scroll target of the program block's "Enroll in Signal
// Advance →". Since BRK-S501 it is the October wireframe's card: title and
// pill, the benefits (1 Lower rates ⓘ, 2 Higher limits ⓘ), the two "Earn up
// to" tiles from the program record's `advanceLimits`, "Steps to enroll" read
// from served states, then the client link rows — Copy link, Send link /
// Resend, Send both, Check link status — and the document alternative
// (`SignalAdvanceEvidence`) for an application still open.
//
// `apply` is the same card on the Financials tab's not-connected state: the
// pitch and the steps with "Apply for Signal Advance →" to this section, and
// no link rows of its own (one `#advance-app` per page).
//
// D4 (accepted): PMS emails the link to the client (PMS-S840, ahead of the
// pin). NO OPTIMISTIC SUCCESS (NN #7): a send says "sent" only from the 2xx,
// and the row's "Sent … · Expires …" line only ever comes from the account
// read PMS serves back afterwards. No send state is kept here (NN #9).
//
// The accounting vendor is never named (BRK-S322, vendorBranding.test.ts);
// Plaid's own branding is its requirement, so the bank row names it.

export interface SignalAdvanceApplicationProps {
  detail: AccountDetail;
  /** The Financials tab's copy: "Apply for Signal Advance →" and no links. */
  apply?: boolean;
  /** The re-read account, after a send or Check link status. */
  onRefreshed: (detail: AccountDetail) => void;
  /** Re-read the account after an evidence upload changed. */
  onChanged: () => void;
}

export function SignalAdvanceApplication({
  detail,
  apply = false,
  onRefreshed,
  onChanged,
}: SignalAdvanceApplicationProps) {
  const headingId = useId();
  const ref = useRef<HTMLElement>(null);
  const location = useLocation();
  const advance = readAdvanceRequirements(detail.advanceRequirements);
  const view = advanceApplication({
    preapproval: detail.preapproval,
    advanceLimits: detail.advanceLimits,
    application: readPreapprovalApplication(detail.preapprovalApplication),
    advance,
  });

  // A deep link to `#advance-app` (the program block's Enroll action) lands
  // here: the router does not scroll to a hash, and this panel may have just
  // been revealed by the same navigation.
  useEffect(() => {
    if (!apply && location.hash === "#advance-app") {
      ref.current?.scrollIntoView?.({ block: "start" });
    }
  }, [apply, location.hash, location.key]);

  return (
    <section
      ref={ref}
      id={apply ? undefined : "advance-app"}
      aria-labelledby={headingId}
      data-testid={apply ? "advance-apply" : "advance-app"}
      // Its own well on the Surety program tab; on Financials it sits in
      // that tab's well, and a well inside a well is what BRK-S502 removed.
      className={`flex w-full min-w-0 scroll-mt-4 flex-col gap-5 ${apply ? "" : WELL}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 id={headingId} className="text-heading text-brk-text">
          {view.title}
        </h2>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-caption font-medium ${pillClass(view)}`}
        >
          {view.pill}
        </span>
      </div>

      <Lead view={view} />

      {view.offer.length > 0 && (
        <>
          <Tiles
            items={view.offer.map((o) => ({
              eyebrow: "Earn up to",
              value: o.value,
              label: o.label,
            }))}
          />
          <p className="text-sm text-brk-dim">
            3x the limits. Lower per-limit pricing. More competitive bids.
            Signal Advance does all three, so there’s no good reason to quote
            without it.
          </p>
        </>
      )}

      {view.limits.length > 0 && (
        <Tiles
          items={view.limits.map((l) => ({ value: l.value, label: l.label }))}
        />
      )}

      <Steps steps={view.steps} />

      {apply
        ? view.stage === "recommended" && (
            <Link
              to={`/surety/accounts/${encodeURIComponent(detail.id)}?tab=program#advance-app`}
              className={`${buttonClass()} self-start`}
            >
              Apply for Signal Advance →
            </Link>
          )
        : view.stage === "recommended" &&
          advance &&
          view.linksOpen && (
            <>
              <ClientLinks
                detail={detail}
                advance={advance}
                onRefreshed={onRefreshed}
              />
              <SignalAdvanceEvidence
                accountId={detail.id}
                evidence={advance}
                onChanged={onChanged}
              />
            </>
          )}
    </section>
  );
}

/** The figure tiles: the program record's "Earn up to" limits, or the
 *  insured's own Advance limits once they hold it. Stacked at 375. */
function Tiles({
  items,
}: {
  items: { eyebrow?: string; value: string; label: string }[];
}) {
  return (
    <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {items.map((t) => (
        <div
          key={t.label}
          className="flex min-w-0 flex-col gap-0.5 rounded-lg bg-brk-bg p-3"
        >
          {t.eyebrow && (
            <span className="text-caption text-brk-dim">{t.eyebrow}</span>
          )}
          <dd className="break-words text-heading text-brk-text">{t.value}</dd>
          <dt className="text-caption font-medium uppercase tracking-wide text-brk-dim">
            {t.label}
          </dt>
        </div>
      ))}
    </dl>
  );
}

function pillClass(view: AdvanceApplicationView): string {
  switch (view.stage) {
    case "enrolled":
      return statusBadgeClass("approved");
    case "in_review":
      return statusBadgeClass("submitted");
    default:
      return "bg-brk-accent text-white";
  }
}

function Lead({ view }: { view: AdvanceApplicationView }) {
  if (view.stage === "enrolled") {
    return (
      <p className="text-sm text-brk-text">
        This insured is on Signal Advance. Its limits and rate are the ones
        Signal granted.
      </p>
    );
  }
  if (view.stage === "in_review") {
    return (
      <p role="status" className="text-sm text-brk-text">
        {ADVANCE_WITH_UNDERWRITER}.
      </p>
    );
  }
  return <SignalAdvancePitch />;
}

/** The benefits, the wireframe's two numbered items, each with an ⓘ that
 *  says it in words — no score, no figure of the SPA's own. */
const BENEFITS = [
  {
    title: "Lower rates",
    about:
      "Advance rates are set from the business’s financials, so qualifying clients pay less than on Signal Express.",
  },
  {
    title: "Higher limits",
    about:
      "Qualifying businesses can bond larger jobs, and more of them at once, than on Signal Express.",
  },
] as const;

export function SignalAdvancePitch() {
  return (
    <div className="flex flex-col gap-3">
      <ul
        aria-label="Benefits of Signal Advance"
        className="flex flex-wrap gap-x-6 gap-y-2"
      >
        {BENEFITS.map((b, i) => (
          <li key={b.title} className="flex items-center gap-2">
            <Num n={i + 1} />
            <span className="text-sm font-medium text-brk-text">{b.title}</span>
            <Tooltip title={b.about}>
              <button
                type="button"
                aria-label={`About ${b.title.toLowerCase()}: ${b.about}`}
                className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-brk-dim hover:bg-brk-bg hover:text-brk-text ${TOUCH_SQUARE}`}
              >
                <Info size={16} aria-hidden />
              </button>
            </Tooltip>
          </li>
        ))}
      </ul>
      <p className="text-sm text-brk-dim">
        Help your clients win more bids with industry-low rates for
        best-in-class clients.
      </p>
    </div>
  );
}

function Num({ n }: { n: number }) {
  return (
    <span
      aria-hidden="true"
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-brk-border text-caption font-semibold text-brk-dim"
    >
      {n}
    </span>
  );
}

const STEP_STATUS: Record<AdvanceStep["tone"], string> = {
  done: "text-green-700 dark:text-green-400",
  waiting: "text-blue-700 dark:text-blue-300",
  todo: "text-brk-dim",
};

function Steps({ steps }: { steps: AdvanceStep[] }) {
  const id = useId();
  return (
    <div className="flex flex-col gap-3">
      <h3 id={id} className="text-sm font-semibold text-brk-text">
        Steps to enroll
      </h3>
      <ol aria-labelledby={id} className="flex flex-col gap-3">
        {steps.map((s, i) => (
          <li key={s.key} className="flex min-w-0 gap-3">
            {s.tone === "done" ? (
              <span
                aria-hidden="true"
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${statusBadgeClass("approved")}`}
              >
                <Check size={14} />
              </span>
            ) : (
              <Num n={i + 1} />
            )}
            <div className="flex min-w-0 flex-col">
              <span className="text-sm font-medium text-brk-text">
                {s.label}
              </span>
              <span className="text-caption text-brk-dim">{s.sub}</span>
              {s.status !== null && (
                <span
                  className={`text-caption font-semibold ${STEP_STATUS[s.tone]}`}
                >
                  {s.status}
                </span>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ── The client link rows ────────────────────────────────────────────────────

type Pending = AdvanceEvidenceRequirement | "both" | "check" | null;

function ClientLinks({
  detail,
  advance,
  onRefreshed,
}: {
  detail: AccountDetail;
  advance: ReadAdvanceRequirements;
  onRefreshed: (detail: AccountDetail) => void;
}) {
  const { authedRequest } = useAuth();
  const headingId = useId();
  // The links' facts are PMS-S840's connections read (BRK-S446), read on
  // mount and again after every send, mint or Check link status.
  const [connections, setConnections] = useState<AccountConnection[] | null>(
    null,
  );
  const [readError, setReadError] = useState<string | null>(null);
  const links = connectionsByKind(connections);
  useEffect(() => {
    let live = true;
    listAccountConnections(authedRequest, detail.id).then(
      (list) => live && setConnections(list),
      (err: unknown) => live && setReadError(messageFor(err)),
    );
    return () => {
      live = false;
    };
  }, [authedRequest, detail.id]);
  const recipients = advanceLinkRecipients(
    detail.personnel ?? [],
    detail.principalOwner,
  );
  const [sendToId, setSendToId] = useState<string | null>(null);
  const recipient: AdvanceLinkRecipient | null =
    recipients.find((r) => r.id === sendToId) ?? recipients[0] ?? null;

  const [pending, setPending] = useState<Pending>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<
    Partial<Record<AdvanceEvidenceRequirement, string>>
  >({});
  const [error, setError] = useState<string | null>(null);

  const now = new Date();
  const rows = ADVANCE_LINK_KINDS.map((kind) => ({
    kind,
    link: links[kind],
    view: advanceLinkRow(advance[kind], links[kind], now),
  }));
  const open = rows.filter((r) => r.view.canAct);

  async function reread() {
    const [fresh, list] = await Promise.all([
      getAccount(authedRequest, detail.id),
      listAccountConnections(authedRequest, detail.id),
    ]);
    setReadError(null);
    setConnections(list);
    onRefreshed(fresh);
  }

  function clear() {
    setNotice(null);
    setError(null);
    setRowErrors({});
  }

  async function send(kind: AdvanceEvidenceRequirement) {
    if (!recipient) return;
    clear();
    setPending(kind);
    try {
      await sendAdvanceLink(authedRequest, detail.id, kind, recipient.email);
    } catch (err) {
      setRowErrors({ [kind]: messageFor(err) });
      setPending(null);
      return;
    }
    setNotice(
      `${ADVANCE_LINK_COPY[kind].name} link sent to ${recipient.email}.`,
    );
    await refreshAfter();
  }

  async function sendBoth() {
    if (!recipient) return;
    clear();
    setPending("both");
    for (const { kind } of open) {
      try {
        await sendAdvanceLink(authedRequest, detail.id, kind, recipient.email);
      } catch (err) {
        // Stop at the first refusal: a link already accepted stays sent, and
        // the re-read below shows which.
        setError(`${ADVANCE_LINK_COPY[kind].name}: ${messageFor(err)}`);
        await refreshAfter();
        return;
      }
    }
    setNotice(`Both links sent to ${recipient.email}.`);
    await refreshAfter();
  }

  async function refreshAfter() {
    try {
      await reread();
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setPending(null);
    }
  }

  async function check() {
    clear();
    setPending("check");
    try {
      await reread();
      setNotice("Link status is up to date.");
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setPending(null);
    }
  }

  /** Mint (or re-mint) a link: the row changes only from PMS's answer. */
  async function mint(
    kind: AdvanceEvidenceRequirement,
  ): Promise<AccountConnection | null> {
    try {
      const minted = await requestAccountConnection(
        authedRequest,
        detail.id,
        kind,
      );
      setConnections((list) => [
        ...(list ?? []).filter((c) => c.kind !== kind),
        minted,
      ]);
      return minted;
    } catch (err) {
      setRowErrors({ [kind]: messageFor(err) });
      return null;
    }
  }

  async function copy(kind: AdvanceEvidenceRequirement) {
    clear();
    setPending(kind);
    // No link yet: PMS mints one first, and only its answer is copied.
    const url = links[kind]?.linkUrl ?? (await mint(kind))?.linkUrl ?? null;
    setPending(null);
    if (url === null) return;
    try {
      await navigator.clipboard.writeText(url);
      setNotice(`${ADVANCE_LINK_COPY[kind].name} link copied.`);
    } catch {
      // No clipboard: claiming a copy that did not happen is worse than
      // silence.
    }
  }

  async function renew(kind: AdvanceEvidenceRequirement) {
    clear();
    setPending(kind);
    const minted = await mint(kind);
    setPending(null);
    if (minted) {
      setNotice(
        `A new ${ADVANCE_LINK_COPY[kind].name.toLowerCase()} link is ready.`,
      );
    }
  }

  const expiries = [
    ...new Set(
      open.map((r) => r.link?.expiresAt ?? null).filter((e) => e !== null),
    ),
  ];
  const allSent = open.every((r) => r.view.sendLabel === "Resend");
  const busy = pending !== null;

  return (
    <div
      aria-labelledby={headingId}
      role="region"
      className="flex flex-col gap-4 border-t border-brk-border pt-4"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 flex-col gap-1">
          <h3 id={headingId} className="text-sm font-semibold text-brk-text">
            Share connection links with the client
          </h3>
          <p className="text-sm text-brk-dim">
            Copy a link to pass on yourself, or have Signal email it.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void check()}
          disabled={busy}
          className={`${buttonClass({ variant: "secondary", size: "sm" })} self-start`}
        >
          {pending === "check" ? "Checking…" : "Check link status"}
        </button>
      </div>

      {recipients.length > 1 ? (
        <div className="sm:max-w-md">
          <SelectField
            label="Send links to"
            value={recipient?.id ?? ""}
            onChange={(e) => setSendToId(e.target.value)}
            options={recipients.map((r) => ({
              value: r.id,
              label: `${r.name} · ${r.email}`,
            }))}
          />
        </div>
      ) : recipient ? (
        <p className="break-words text-sm text-brk-dim">
          Sends to {recipient.name} · {recipient.email}
        </p>
      ) : (
        <p className="text-sm text-brk-dim">
          No one on this account has an email of record, so Signal can’t email
          the links. Add one on the Owners &amp; principals tab, or copy a link
          to pass on yourself.
        </p>
      )}

      {readError && (
        <p role="alert" className="text-sm text-brk-danger">
          {readError}
        </p>
      )}
      <ul className="flex flex-col gap-3">
        {rows.map(({ kind, view }) => (
          <LinkRow
            key={kind}
            kind={kind}
            view={view}
            busy={busy}
            sending={pending === kind}
            canSend={recipient !== null}
            error={rowErrors[kind] ?? null}
            onSend={() => void send(kind)}
            onCopy={() => void copy(kind)}
            onRenew={() => void renew(kind)}
          />
        ))}
      </ul>

      {notice && (
        <p role="status" className="text-sm text-brk-text">
          {notice}
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-brk-dim">
          {expiries.length === 1 &&
            `Links stay live until ${formatShortDate(expiries[0])}. `}
          Once the client connects, check link status to see the steps above
          update.
        </p>
        {open.length > 1 && (
          <button
            type="button"
            onClick={() => void sendBoth()}
            disabled={busy || recipient === null}
            className={`${buttonClass()} self-start sm:self-auto`}
          >
            {pending === "both"
              ? "Sending…"
              : allSent
                ? "Resend both links"
                : "Send both links"}
          </button>
        )}
      </div>
    </div>
  );
}

function LinkRow({
  kind,
  view,
  busy,
  sending,
  canSend,
  error,
  onSend,
  onCopy,
  onRenew,
}: {
  kind: AdvanceEvidenceRequirement;
  view: ReturnType<typeof advanceLinkRow>;
  busy: boolean;
  sending: boolean;
  canSend: boolean;
  error: string | null;
  onSend: () => void;
  onCopy: () => void;
  onRenew: () => void;
}) {
  const copy = ADVANCE_LINK_COPY[kind];
  const nameId = useId();
  return (
    <li
      role="group"
      aria-labelledby={nameId}
      className="flex min-w-0 flex-col gap-3 rounded-lg border border-brk-border p-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <span id={nameId} className="text-sm font-medium text-brk-text">
          {copy.name}
        </span>
        <span className="text-caption text-brk-dim">{copy.sub}</span>
        <span className="flex items-center gap-2 text-caption font-semibold text-brk-text">
          <span
            aria-hidden="true"
            className={`h-2 w-2 shrink-0 rounded-full ${
              view.connected ? "bg-green-600" : "bg-brk-dim"
            }`}
          />
          {view.status}
        </span>
        {view.detail && (
          <span className="text-caption text-brk-dim">{view.detail}</span>
        )}
        {error && (
          <p role="alert" className="text-sm text-brk-danger">
            {error}
          </p>
        )}
      </div>
      {view.expired && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onRenew}
            disabled={busy}
            aria-label={`Create a new link — ${copy.name}`}
            className={buttonClass({ variant: "secondary", size: "sm" })}
          >
            {sending ? "Creating…" : "Create a new link"}
          </button>
        </div>
      )}
      {view.canAct && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onCopy}
            disabled={busy}
            aria-label={`Copy link — ${copy.name}`}
            className={buttonClass({ variant: "secondary", size: "sm" })}
          >
            Copy link
          </button>
          <button
            type="button"
            onClick={onSend}
            disabled={busy || !canSend}
            aria-label={`${view.sendLabel} — ${copy.name}`}
            className={buttonClass({ variant: "secondary", size: "sm" })}
          >
            {sending ? "Sending…" : view.sendLabel}
          </button>
        </div>
      )}
    </li>
  );
}
